import React, { FC, useEffect, useState } from 'react'
import { LinkProps } from 'next/link'
import { ReactHoverObserver } from '@docpace/shared-react-components/ReactHoverObserver'

import { 
    practiceOverviewTooltipText,
    workflowQualityTooltipText,
    growthTooltipText,
    appointmentDurationsTooltipText,
    workingHoursTooltipText,
    patientBehaviorTooltipText,
    templateViewTooltipText,
    patientFlowReportTooltipText,
    monthlyReportTooltipText,
    checkoutTableTooltipText,
} from './ManagerSidebar.constants'
import clsx from 'clsx'
import Link from 'next/link'
import { Tooltip } from '@docpace/shared-react-components/Tooltip'
import { Divider } from '@docpace/shared-react-components/Divider'
import { Drawer } from '@docpace/shared-react-components/Drawer'
import managerRoutes from '@docpace/shared-react-routes/managerRoutes'
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    PushpinPinnedIcon,
    PushpinUnpinnedIcon,
} from '@docpace/shared-react-icons'
import { MarkdownRenderer } from '@docpace/shared-react-components/MarkdownRenderer'
import {
    MANAGER_SIDEBAR_DRAWER_WIDTH,
    MANAGER_SIDEBAR_DRAWER_WIDTH_CLOSED,
} from '@docpace/manager-react-components/ManagerLayout'

export interface ManagerSidebarProps {
    practiceId?: string
    providerId?: string
    isOpen: boolean
    isPinned: boolean
    isHovering?: boolean
    showInsightsLinks: boolean
    insightsRequiresUpgrade: boolean
    showMonthlyReport: boolean
    showPatientFlowReport: boolean
    showCheckoutTable: boolean
    setIsSidebarPinned: (isSidebarPinned: boolean) => void
    setIsSidebarOpen: (isSidebarOpen: boolean) => void
}

export interface ManagerSidebarLinkProps {
    title: string
    Icon?: any
    linkProps?: LinkProps
    hideIcon?: boolean
    isOpen: boolean
    onHover?: (isHovering: boolean)=>void
    tooltipText?: string | any
    isHovering?: boolean
    disabled?: boolean
    dense?: boolean
    endIcon?: any
    onClick?: any
    id?: string
    tooltipClassName?: string
}

const ListItemText = ({ 
    primary, 
    className, 
}:{
    primary: string
    className: string
})=>{
    const style: any = { textWrap: 'nowrap'}
    return <div className={className} style={style}>
        {primary}
    </div>
}


const MuiSidebarNavListIcon = React.memo(
    Object.assign(
        (props: any) => {
            const { linkProps, disabled, Icon } = props
            const iconComponent = (
                <div className={clsx( disabled ? 'cursor-not-allowed' : 'cursor-pointer', 'w-6 m-0 mr-2 text-gray-600 dark:text-white')}>
                    <Icon />
                </div>
            )
            return (linkProps && !disabled) ? (
                <Link {...linkProps}>
                    {iconComponent}
                </Link>
            ) : (
                iconComponent
            )
        },
        { displayName: 'MuiSidebarNavListIcon' }
    )
)

const MuiSidebarNavListItemText = React.memo(
    Object.assign(
        (props: any) => {
            const { title, classes, linkProps, disabled } = props
            const titleComponent = (
                <ListItemText
                    primary={title}
                    className={'mx-0 my-4 font-sm text-gray-600 dark:text-white'}
                />
            )
            return (linkProps && !disabled) ? (
                <Link {...linkProps}>
                    {titleComponent}
                </Link>
            ) : (
                titleComponent 
            )
        },
        { displayName: 'MuiSidebarNavListItemText' }
    )
)

export const ManagerSidebarLink: FC<ManagerSidebarLinkProps> = (props) => {
    const { isOpen, title, onClick, Icon, isHovering, onHover, tooltipText, hideIcon, disabled, dense, endIcon, tooltipClassName } = props
    useEffect(()=>{
        if(typeof isHovering === 'boolean' && onHover){
            onHover(isHovering)
        }
    }, [isHovering])
    const textProps = { ...props, disabled: undefined }
    const _ListItem = <div
            key={title}
            onClick={onClick}
            className={clsx(
                'h-10 cursor-pointer flex items-center w-full', 
                hideIcon && 'm-0 p-0', 
                disabled && 'disabled', 
                dense && 'h-10',
            )}            // disabled={disabled}
            // dense={dense}
        >
            {Icon && <MuiSidebarNavListIcon {...textProps} />}
            <MuiSidebarNavListItemText {...props} />
            {endIcon}
        </div>

    return tooltipText ? <Tooltip 
        placement={isOpen ? 'right' : 'right-start'}
        className={clsx('sidebar-md-content max-w-lg!')} 
        title={<MarkdownRenderer
            content={tooltipText}   
        />}
    >{ _ListItem }</Tooltip> : _ListItem
    // return _ListItem
}

const ManagerSidebarInsightsTeaserTooltip = ({ isOpen, insightsRequiresUpgrade, children })=>{
    return !isOpen && insightsRequiresUpgrade 
        ? <Tooltip placement={isOpen ? 'right' : 'right-start'} title={<div
            className={clsx(
                // 'bg-white bg-center border border-solid rounded-lg',
                // 'flex flex-col space-y-1 items-center cursor-pointer',
                // 'transition-opacity duration-500 ease-in-out', 
            )} 
            // style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
            <div>
                <div className='text-center font-semibold'>DOCPACE Insights</div>
                <div>(Premium Feature)</div>
                <div>Click any icon to learn more</div>
                </div>
            </div>}><div>{ children }</div></Tooltip>
        : <div>{ children }</div>
}

export const ManagerSidebar: FC<ManagerSidebarProps> = (props) => {
    const {
        practiceId,
        providerId,
        isOpen,
        isPinned,
        isHovering,
        setIsSidebarOpen,
        setIsSidebarPinned,
        showInsightsLinks,
        insightsRequiresUpgrade,
        showMonthlyReport,
        showPatientFlowReport,
        showCheckoutTable
    } = props
    const MenuTrayIcon = () => {
        const isLtr = true //todo
        const isChevronLeft = !(isLtr && isOpen) && (isLtr || isOpen)
        return (
            <button
                id='sidebar-tray-button'
                onClick={() => setIsSidebarOpen(!isOpen)}
                className='text-gray-600 dark:text-white'
            >
                {isChevronLeft ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </button>
        )
    }
    const PushpinIcon = () => {
        const style = { fontSize: '0.9rem', right: 5 }
        return (
            <button className='text-gray-600 dark:text-white'>
                {isPinned ? (
                    <PushpinPinnedIcon style={style} />
                ) : (
                    <PushpinUnpinnedIcon style={style} />
                )}
            </button>
        )
    }

    useEffect(() => {
        !isPinned && setIsSidebarOpen(!!isHovering)
    }, [isHovering, isPinned])
    
    if (!practiceId) return <></>

    return (
        <>
            <style>{`

                .sidebar-md-content ul li::marker, .sidebar-md-content ul, .sidebar-md-content p {
                    color: white;
                }

                .sidebar-md-content ul {
                    line-height: 1;
                    text-align: left;
                }

            `}</style>
            <Drawer
                id={'manager-sidebar'}
                variant="permanent"
                isOpen={isOpen}
                isHovering={isHovering}
                openWidth={MANAGER_SIDEBAR_DRAWER_WIDTH}
                closedWidth={MANAGER_SIDEBAR_DRAWER_WIDTH_CLOSED}
                className='!overflow-hidden'
            >
                <div className='flex-1 flex flex-col' style={{ width: MANAGER_SIDEBAR_DRAWER_WIDTH }}>
                    <div className='w-full pt-4 list-none' style={{ height: 63 }}>
                        <div className='flex items-center ml-4 my-2'>
                            <MenuTrayIcon />
                        </div>
                    </div>
                    <Divider />
                    <div className='ml-4 w-full mt-4 flex-1 flex justify-start flex-col list-none'>
                        
                        { (showCheckoutTable || showMonthlyReport) && (
                            <div className='relative pl-12 text-sm font-bold h-2 text-gray-600 dark:text-white' style={{ top: -12 }}>
                                DOCPACE® Manager
                            </div>
                        )}
                        
                        { showCheckoutTable && <>
                            <ManagerSidebarLink
                                title="Appointments"
                                isOpen={isOpen}
                                Icon={managerRoutes.Index.opts.icon}
                                tooltipText={checkoutTableTooltipText}
                                linkProps={{
                                    href: providerId
                                        ? managerRoutes.ManagerProviderByIdIndex.hrefNext({
                                            providerId,
                                        })
                                        : managerRoutes.Index.hrefNext(),
                                }}
                            />
                        </> }
                        { showMonthlyReport && <ManagerSidebarLink
                            title="Monthly Report"
                            isOpen={isOpen}
                            tooltipText={monthlyReportTooltipText}
                            linkProps={{
                                href: managerRoutes.ManagerMonthlyReportAnalytics.hrefNext(),
                            }}
                            Icon={managerRoutes.ManagerMonthlyReportAnalytics.opts.icon}
                        /> }
                    </div>                    

                    { (showCheckoutTable || showMonthlyReport) && <Divider/> }

                    <div className='ml-4 w-full flex-1 flex justify-start flex-col list-none'>
                        { showInsightsLinks && (
                            <div className='relative pl-12 text-sm font-bold h-6 text-gray-600 dark:text-white' style={{ top: 4 }}>
                                DOCPACE® Insights
                            </div>
                        )}
                        {/* <ManagerSidebarLink title='Appointment Types'
                            linkProps={{ href: managerRoutes.ManagerAppointmentTypeIndex.hrefNext() }} 
                            Icon={managerRoutes.ManagerAppointmentTypeIndex.opts.icon} 
                        /> */}
                        {/* <div className='divider'/> */}
                        {/* <ManagerSidebarLink
                            title="Reports"
                            linkProps={{
                                href: '#',
                            }}
                            Icon={managerRoutes.ManagerAnalyticsIndex.opts.icon}
                        /> */}
                        {/* <ManagerSidebarLink
                            title="Practice Overview"
                            tooltipText={null}
                            disabled={false}
                            linkProps={{
                                href: managerRoutes.ManagerAnalyticsIndex.hrefNext(),
                            }}
                            Icon={managerRoutes.ManagerAnalyticsIndex.opts.icon}
                        /> */}
                        { showInsightsLinks && <ManagerSidebarInsightsTeaserTooltip isOpen={isOpen} insightsRequiresUpgrade={insightsRequiresUpgrade}>
                            { insightsRequiresUpgrade && <Link
                                href={managerRoutes.ManagerAnalyticsUpgrade.hrefNext()}
                                className={clsx(
                                    'absolute bg-white dark:bg-base-100 bg-center border border-solid dark:border-gray-600 rounded-lg',
                                    'flex flex-col p-2 ml-2 items-center z-10 cursor-pointer',
                                    'text-gray-600 dark:text-white',
                                    'transition-opacity duration-500 ease-in-out',
                                    !isOpen ? 'opacity-0' : 'opacity-100'
                                )}
                                style={{ top: 280 }}
                            >
                                <div className='text-center font-semibold'>DOCPACE Insights</div>
                                <div>(Premium Feature)</div>
                                <div>Click <span className='underline'>here</span> to learn more</div>
                            </Link> }
                            <div className={clsx(insightsRequiresUpgrade && 'opacity-50')}>
                                <ReactHoverObserver>
                                    <ManagerSidebarLink
                                        title="Practice Overview"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : practiceOverviewTooltipText}
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerAnalyticsIndex.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerAnalyticsIndex.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Workflow Quality"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : workflowQualityTooltipText }
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerPerformanceAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerPerformanceAnalytics.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Growth Analytics"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : growthTooltipText }
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerGrowthAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerGrowthAnalytics.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Appt Durations"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : appointmentDurationsTooltipText }
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerDeliveryAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerDeliveryAnalytics.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Working Hours"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : workingHoursTooltipText }
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerProductivityAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerProductivityAnalytics.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Patient Behavior"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : patientBehaviorTooltipText }
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerAdherenceAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerAdherenceAnalytics.opts.icon}
                                    />
                                    <ManagerSidebarLink
                                        title="Template View"
                                        isOpen={isOpen}
                                        tooltipText={insightsRequiresUpgrade ? null : templateViewTooltipText}
                                        linkProps={{
                                            href: insightsRequiresUpgrade ? managerRoutes.ManagerAnalyticsUpgrade.hrefNext() 
                                                : managerRoutes.ManagerProviderSummaryAnalytics.hrefNext(),
                                        }}
                                        Icon={managerRoutes.ManagerProviderSummaryAnalytics.opts.icon}
                                    />
                                    <div className='h-4'/>
                                </ReactHoverObserver>
                            </div>
                        </ManagerSidebarInsightsTeaserTooltip>}
                    </div>                    
                    <Divider />
                    <div className='ml-4 w-full flex-1 flex justify-start flex-col list-none'>
                        { showPatientFlowReport && 
                            <ManagerSidebarLink
                                title="Patient Flow Report™"
                                isOpen={isOpen}
                                tooltipText={patientFlowReportTooltipText}
                                linkProps={{
                                    href: managerRoutes.ManagerPatientFlowAnalytics.hrefNext(),
                                }}
                                Icon={managerRoutes.ManagerPatientFlowAnalytics.opts.icon}
                            />
                        }
                    </div>
                </div>
                <div className='flex flex-1 flex-col justify-end list-none' style={{ width: MANAGER_SIDEBAR_DRAWER_WIDTH }}>
                    <div style={{ position: 'relative', right: -20, top: -8 }}>
                        <ManagerSidebarLink
                            title={`${isPinned ? 'Unpin' : 'Pin'} Sidebar`}
                            isOpen={isOpen}
                            id='sidebar-pushpin-button'
                            Icon={PushpinIcon}
                            onClick={() => setIsSidebarPinned(!isPinned)}
                        />
                    </div>
                </div>
            </Drawer>
        </>
    )
}
